import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link, useParams } from 'react-router-dom'
import { PortableText } from '@portabletext/react'
import {
  fetchPostBySlug, fetchRelatedPosts,
  urlFor, formatDate, formatDateShort,
  getBadgeColors, SEGMENT_GRADIENT,
  extractToc, slugifyHeading,
} from '../lib/sanityClient'

const DEFAULT_BIO = 'FrameFinder is a Denver-based marketplace connecting local businesses with vetted freelance photographers and videographers. Our editorial team writes about the local photography market, creative business strategy, and how Denver\'s visual content industry actually works.'

// ── Portable Text components ──────────────────────────────────────────────────
const makeComponents = () => ({
  types: {
    statCallout: ({ value }) => (
      <div className="stat-callout" aria-label="Key statistic">
        <div className="stat-callout-number">{value.statNumber}</div>
        <div className="stat-callout-text">
          <strong>{value.statHeadline}</strong>{' '}{value.statBody}
        </div>
      </div>
    ),
    inlineCta: ({ value }) => (
      <div className="inline-cta">
        <div className="inline-cta-text">
          <div className="inline-cta-eyebrow">{value.ctaEyebrow}</div>
          <div className="inline-cta-headline">{value.ctaHeadline}</div>
          <div className="inline-cta-desc">{value.ctaDescription}</div>
        </div>
        <a href={value.ctaButtonUrl} className="btn-accent">
          {value.ctaButtonLabel}
          <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </a>
      </div>
    ),
  },
  block: {
    normal: ({ children }) => <p>{children}</p>,
    h2: ({ children, value }) => {
      const text = (value.children || []).map(c => c.text || '').join('')
      return <h2 id={slugifyHeading(text)}>{children}</h2>
    },
    h3: ({ children, value }) => {
      const text = (value.children || []).map(c => c.text || '').join('')
      return <h3 id={slugifyHeading(text)}>{children}</h3>
    },
    blockquote: ({ children }) => <blockquote><p>{children}</p></blockquote>,
  },
  marks: {
    strong: ({ children }) => <strong>{children}</strong>,
    em: ({ children }) => <em>{children}</em>,
    link: ({ children, value }) => (
      <a href={value?.href} target={value?.href?.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer">
        {children}
      </a>
    ),
  },
  list: {
    bullet: ({ children }) => <ul>{children}</ul>,
    number: ({ children }) => <ol>{children}</ol>,
  },
  listItem: {
    bullet: ({ children }) => <li>{children}</li>,
    number: ({ children }) => <li>{children}</li>,
  },
})

// ── Component ─────────────────────────────────────────────────────────────────
const BlogPost = () => {
  const { slug } = useParams()
  const [post, setPost] = useState(null)
  const [related, setRelated] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeId, setActiveId] = useState('key-takeaways')
  const progressRef = useRef(null)
  const ptComponents = makeComponents()

  useEffect(() => {
    setLoading(true)
    fetchPostBySlug(slug).then(async data => {
      setPost(data)
      setLoading(false)
      if (!data) return
      // Related posts: use manual picks first, fill from category
      let rel = data.relatedPosts || []
      if (rel.length < 3) {
        const fallback = await fetchRelatedPosts(data.category?.slug, slug)
        const existing = new Set(rel.map(r => r.slug))
        const extra = fallback.filter(f => !existing.has(f.slug))
        rel = [...rel, ...extra].slice(0, 3)
      }
      setRelated(rel)
    })
  }, [slug])

  const handleScroll = useCallback(() => {
    // Progress bar
    const article = document.getElementById('article-body')
    if (article && progressRef.current) {
      const top = article.getBoundingClientRect().top + window.scrollY
      const pct = Math.min(100, Math.max(0, ((window.scrollY - top) / article.offsetHeight) * 100))
      progressRef.current.style.width = pct + '%'
    }
    // Active TOC
    const headings = document.querySelectorAll('h2[id], h3[id], [id="key-takeaways"]')
    let current = 'key-takeaways'
    headings.forEach(el => {
      if (el.getBoundingClientRect().top <= 100) current = el.id
    })
    setActiveId(current)
  }, [])

  useEffect(() => {
    if (!post) return
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [post, handleScroll])

  const scrollToSection = (e, id) => {
    e.preventDefault()
    const el = document.getElementById(id)
    if (!el) return
    const top = el.getBoundingClientRect().top + window.scrollY - 80
    window.scrollTo({ top, behavior: 'smooth' })
  }

  const copyLink = (e) => {
    e.preventDefault()
    navigator.clipboard?.writeText(window.location.href).then(() => {
      const btn = e.currentTarget
      btn.style.background = 'var(--ink)'
      btn.style.color = 'var(--white)'
      setTimeout(() => { btn.style.background = ''; btn.style.color = '' }, 1200)
    })
  }

  if (loading) return (
    <>
      <style>{`body{font-family:'DM Sans',sans-serif;background:#f7f5f2;}`}</style>
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a0a0a0', fontSize: 14 }}>Loading...</div>
    </>
  )

  if (!post) return (
    <>
      <style>{`body{font-family:'DM Sans',sans-serif;background:#f7f5f2;}`}</style>
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a0a0a0', fontSize: 14 }}>Post not found.</div>
    </>
  )

  const toc = extractToc(post.body)
  const badge = getBadgeColors(post.category)
  const publishedDate = formatDate(post.publishedAt)
  const heroSrc = post.heroImage ? urlFor(post.heroImage).width(1360).height(765).url() : null
  const ogSrc = post.ogImage ? urlFor(post.ogImage).width(1200).height(630).url() : heroSrc
  const relatedLabel = post.relatedSectionLabel || post.category?.sectionLabel || 'More Posts'

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BlogPosting',
        '@id': `${post.seoCanonicalUrl}#article`,
        headline: post.title,
        description: post.seoDescription,
        url: post.seoCanonicalUrl,
        datePublished: post.publishedAt,
        dateModified: post.modifiedAt || post.publishedAt,
        author: { '@type': 'Organization', name: 'FrameFinder Editorial', url: 'https://framefinder.io' },
        publisher: { '@type': 'Organization', name: 'FrameFinder', url: 'https://framefinder.io' },
        ...(ogSrc && { image: { '@type': 'ImageObject', url: ogSrc, width: 1200, height: 630 } }),
        mainEntityOfPage: { '@type': 'WebPage', '@id': post.seoCanonicalUrl },
        keywords: post.keywords || [],
        articleSection: post.articleSection,
        ...(post.wordCount && { wordCount: post.wordCount }),
        inLanguage: 'en-US',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://framefinder.io' },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://framefinder.io/blog' },
          ...(post.category ? [{ '@type': 'ListItem', position: 3, name: post.category.title, item: `https://framefinder.io/blog/category/${post.category.slug}` }] : []),
          { '@type': 'ListItem', position: post.category ? 4 : 3, name: post.title, item: post.seoCanonicalUrl },
        ],
      },
    ],
  }

  return (
    <>
      <Helmet>
        <title>{post.seoTitle}</title>
        <meta name="description" content={post.seoDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={post.seoCanonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.seoDescription} />
        <meta property="og:url" content={post.seoCanonicalUrl} />
        {ogSrc && <meta property="og:image" content={ogSrc} />}
        <meta property="og:site_name" content="FrameFinder" />
        {post.publishedAt && <meta property="article:published_time" content={post.publishedAt} />}
        {(post.modifiedAt || post.publishedAt) && <meta property="article:modified_time" content={post.modifiedAt || post.publishedAt} />}
        <meta property="article:author" content="FrameFinder Editorial" />
        {post.articleSection && <meta property="article:section" content={post.articleSection} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.seoDescription} />
        {ogSrc && <meta name="twitter:image" content={ogSrc} />}
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        :root {
          --ink: #0e0e0e; --ink-soft: #5a5a5a; --ink-faint: #a0a0a0;
          --paper: #f7f5f2; --white: #ffffff; --accent: #d4a843;
          --accent-soft: #f5ead0; --accent-deep: #8a6a1a; --border: #e4e1db; --border-deep: #c8c3bb;
          --shadow-sm: 0 1px 4px rgba(14,14,14,0.06); --shadow-md: 0 4px 20px rgba(14,14,14,0.08);
          --r-xs: 4px; --r-sm: 8px; --r-md: 14px; --r-lg: 24px; --r-pill: 100px;
          --font-display: 'Instrument Serif', serif; --font-body: 'DM Sans', sans-serif;
          --content-width: 680px;
        }
        html { scroll-behavior: smooth; }
        body { font-family: var(--font-body); background: var(--paper); color: var(--ink); -webkit-font-smoothing: antialiased; line-height: 1.6; }

        #progress-bar { position: fixed; top: 0; left: 0; height: 3px; width: 0%; background: var(--accent); z-index: 200; transition: width 0.1s linear; }

        /* NAV */
        .nav { position: sticky; top: 0; z-index: 100; background: rgba(247,245,242,0.92); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); border-bottom: 1px solid var(--border); height: 60px; display: flex; align-items: center; padding: 0 40px; }
        .nav-inner { max-width: 1280px; margin: 0 auto; width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 24px; }
        .nav-logo { font-family: var(--font-display); font-size: 18px; letter-spacing: -0.3px; color: var(--ink); text-decoration: none; flex-shrink: 0; }
        .nav-links { display: flex; align-items: center; gap: 4px; }
        .nav-link { font-size: 13px; font-weight: 500; color: var(--ink-soft); text-decoration: none; padding: 6px 12px; border-radius: var(--r-pill); transition: color 0.15s, background 0.15s; }
        .nav-link:hover { color: var(--ink); background: rgba(14,14,14,0.05); }
        .nav-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
        .btn { display: inline-flex; align-items: center; gap: 7px; font-family: var(--font-body); font-weight: 500; border: none; cursor: pointer; text-decoration: none; white-space: nowrap; transition: transform 0.15s, box-shadow 0.15s, background 0.15s; border-radius: var(--r-pill); }
        .btn:hover { transform: translateY(-1px); }
        .btn-ghost { font-size: 13px; padding: 7px 16px; background: var(--white); color: var(--ink); border: 1px solid var(--border); }
        .btn-ghost:hover { background: var(--paper); }
        .btn-primary { font-size: 13px; padding: 8px 18px; background: var(--ink); color: var(--white); box-shadow: 0 2px 10px rgba(14,14,14,0.16); }
        .btn-primary:hover { box-shadow: 0 4px 16px rgba(14,14,14,0.24); }

        /* BREADCRUMB */
        .breadcrumb-bar { border-bottom: 1px solid var(--border); background: var(--white); padding: 0 40px; }
        .breadcrumb-inner { max-width: 1280px; margin: 0 auto; height: 44px; display: flex; align-items: center; gap: 6px; }
        .breadcrumb-item { font-size: 12px; color: var(--ink-faint); text-decoration: none; transition: color 0.15s; }
        .breadcrumb-item:hover { color: var(--ink); }
        .breadcrumb-item.current { color: var(--ink-soft); }
        .breadcrumb-sep { color: var(--border-deep); font-size: 12px; user-select: none; }

        /* LAYOUT */
        .page-wrap { max-width: 1280px; margin: 0 auto; padding: 0 40px 120px; }
        .post-header-section { padding: 56px 0 0; max-width: var(--content-width); margin-left: auto; margin-right: auto; }
        .content-row { display: grid; grid-template-columns: var(--content-width) 1fr; gap: 64px; align-items: start; margin-top: 48px; }

        /* POST HEADER */
        .post-cat-badge { display: inline-flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 700; letter-spacing: 0.5px; padding: 5px 12px; border-radius: var(--r-pill); margin-bottom: 20px; text-decoration: none; }
        .cat-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
        .post-title { font-family: var(--font-display); font-size: clamp(32px, 3.5vw, 46px); letter-spacing: -1.5px; line-height: 1.12; color: var(--ink); margin-bottom: 20px; }
        .post-title em { font-style: italic; color: var(--ink-soft); }
        .post-intro { font-size: 18px; line-height: 1.7; color: var(--ink-soft); margin-bottom: 28px; font-weight: 400; }
        .post-meta-row { display: flex; align-items: center; gap: 16px; padding: 20px 0; border-top: 1px solid var(--border); border-bottom: 1px solid var(--border); flex-wrap: wrap; }
        .meta-author { display: flex; align-items: center; gap: 10px; }
        .meta-avatar { width: 34px; height: 34px; border-radius: 50%; background: var(--ink); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .meta-avatar-text { font-size: 11px; font-weight: 600; color: var(--white); font-family: var(--font-display); font-style: italic; }
        .meta-author-name { font-size: 13px; font-weight: 600; color: var(--ink); }
        .meta-dot { width: 3px; height: 3px; border-radius: 50%; background: var(--border-deep); flex-shrink: 0; }
        .meta-item { font-size: 12px; color: var(--ink-faint); }
        .meta-share { margin-left: auto; display: flex; align-items: center; gap: 6px; }
        .share-label { font-size: 11px; color: var(--ink-faint); font-weight: 500; }
        .share-btn { width: 30px; height: 30px; border-radius: 50%; background: var(--paper); border: 1px solid var(--border); display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.15s; color: var(--ink-soft); text-decoration: none; }
        .share-btn:hover { background: var(--ink); color: var(--white); border-color: var(--ink); }

        /* HERO */
        .hero-image { width: 100%; aspect-ratio: 16/9; border-radius: var(--r-lg); overflow: hidden; margin: 36px 0 0; display: flex; align-items: center; justify-content: center; position: relative; }
        .hero-image img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .hero-caption { font-size: 12px; color: var(--ink-faint); margin-top: 10px; text-align: center; font-style: italic; }

        /* ARTICLE */
        article.post-body { font-size: 16.5px; line-height: 1.8; color: var(--ink); }
        article.post-body > * + * { margin-top: 1.5em; }
        article.post-body h2 { font-family: var(--font-display); font-size: 26px; font-weight: 400; letter-spacing: -0.6px; line-height: 1.2; color: var(--ink); margin-top: 2.5em; margin-bottom: 0.6em; padding-top: 0.5em; border-top: 1px solid var(--border); }
        article.post-body h3 { font-family: var(--font-display); font-size: 20px; font-weight: 400; color: var(--ink); margin-top: 2em; margin-bottom: 0.4em; letter-spacing: -0.3px; }
        article.post-body p { margin-top: 1.2em; }
        article.post-body p:first-child { margin-top: 0; }
        article.post-body a { color: var(--ink); text-decoration: underline; text-underline-offset: 3px; text-decoration-color: var(--accent); transition: text-decoration-color 0.15s; }
        article.post-body a:hover { text-decoration-color: var(--ink); }
        article.post-body ul, article.post-body ol { padding-left: 1.5em; margin-top: 1em; }
        article.post-body li { margin-top: 0.5em; }
        article.post-body li::marker { color: var(--accent); }
        article.post-body strong { font-weight: 600; color: var(--ink); }
        article.post-body em { font-style: italic; }
        article.post-body blockquote { border-left: 3px solid var(--accent); padding: 4px 0 4px 24px; margin: 2em 0; }
        article.post-body blockquote p { font-family: var(--font-display); font-size: 20px; font-style: italic; line-height: 1.5; color: var(--ink-soft); }
        article.post-body blockquote cite { display: block; margin-top: 8px; font-size: 12px; font-style: normal; color: var(--ink-faint); }

        /* TAKEAWAYS */
        .takeaways-box { background: var(--accent-soft); border: 1px solid #e8d49a; border-radius: var(--r-md); padding: 28px 32px; margin: 2.5em 0; }
        .takeaways-label { font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--accent-deep); margin-bottom: 14px; }
        .takeaways-box ul { padding-left: 1.4em; margin: 0; }
        .takeaways-box li { font-size: 15px; line-height: 1.65; color: var(--ink); margin-top: 8px; }
        .takeaways-box li:first-child { margin-top: 0; }
        .takeaways-box li::marker { color: var(--accent); }

        /* STAT CALLOUT */
        .stat-callout { background: var(--ink); border-radius: var(--r-md); padding: 28px 32px; margin: 2.5em 0; display: flex; align-items: flex-start; gap: 24px; }
        .stat-callout-number { font-family: var(--font-display); font-size: 48px; color: var(--accent); line-height: 1; flex-shrink: 0; letter-spacing: -2px; }
        .stat-callout-text { font-size: 15px; color: rgba(255,255,255,0.7); line-height: 1.65; }
        .stat-callout-text strong { color: var(--white); }

        /* INLINE CTA */
        .inline-cta { background: var(--white); border: 1px solid var(--border); border-radius: var(--r-lg); padding: 32px; margin: 3em 0; display: flex; align-items: center; justify-content: space-between; gap: 24px; flex-wrap: wrap; }
        .inline-cta-eyebrow { font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--accent-deep); margin-bottom: 8px; }
        .inline-cta-headline { font-family: var(--font-display); font-size: 20px; letter-spacing: -0.4px; color: var(--ink); margin-bottom: 6px; }
        .inline-cta-desc { font-size: 13px; color: var(--ink-soft); line-height: 1.6; }
        .btn-accent { font-size: 14px; font-weight: 500; padding: 11px 24px; background: var(--accent); color: var(--ink); border: none; border-radius: var(--r-pill); cursor: pointer; font-family: var(--font-body); transition: opacity 0.15s, transform 0.15s; text-decoration: none; display: inline-flex; align-items: center; gap: 8px; white-space: nowrap; flex-shrink: 0; }
        .btn-accent:hover { opacity: 0.88; transform: translateY(-1px); }

        /* POST FOOTER */
        .post-footer { margin-top: 56px; padding-top: 40px; border-top: 1px solid var(--border); }
        .post-tags { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 40px; }
        .tags-label { font-size: 12px; font-weight: 600; color: var(--ink-faint); }
        .tag { font-size: 12px; font-weight: 500; padding: 5px 12px; border-radius: var(--r-pill); background: var(--white); border: 1px solid var(--border); color: var(--ink-soft); text-decoration: none; transition: all 0.15s; }
        .tag:hover { border-color: var(--ink); color: var(--ink); }
        .author-bio { background: var(--white); border: 1px solid var(--border); border-radius: var(--r-lg); padding: 28px 32px; display: flex; align-items: flex-start; gap: 20px; margin-bottom: 40px; }
        .author-bio-avatar { width: 52px; height: 52px; border-radius: 50%; background: var(--ink); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .author-bio-avatar-text { font-family: var(--font-display); font-size: 16px; color: var(--white); font-style: italic; }
        .author-bio-name { font-size: 14px; font-weight: 600; color: var(--ink); margin-bottom: 4px; }
        .author-bio-role { font-size: 12px; color: var(--ink-faint); margin-bottom: 10px; }
        .author-bio-text { font-size: 13px; color: var(--ink-soft); line-height: 1.65; }

        /* SIDEBAR */
        .sidebar { position: sticky; top: 80px; display: flex; flex-direction: column; gap: 24px; }
        .toc { background: var(--white); border: 1px solid var(--border); border-radius: var(--r-lg); padding: 24px; }
        .toc-heading { font-size: 11px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--ink-faint); margin-bottom: 16px; }
        .toc-list { list-style: none; display: flex; flex-direction: column; gap: 2px; }
        .toc-link { display: block; font-size: 13px; font-weight: 500; color: var(--ink-soft); text-decoration: none; padding: 6px 10px; border-radius: var(--r-sm); border-left: 2px solid transparent; transition: all 0.15s; line-height: 1.4; }
        .toc-link:hover { color: var(--ink); background: var(--paper); border-left-color: var(--border-deep); }
        .toc-link.active { color: var(--ink); background: var(--accent-soft); border-left-color: var(--accent); font-weight: 600; }
        .toc-link-sub { padding-left: 22px; font-size: 12px; font-weight: 400; }
        .sidebar-cta { background: var(--ink); border-radius: var(--r-lg); padding: 28px 24px; position: relative; overflow: hidden; }
        .sidebar-cta::before { content: ''; position: absolute; right: -40px; top: -40px; width: 160px; height: 160px; background: radial-gradient(circle, rgba(212,168,67,0.15) 0%, transparent 70%); pointer-events: none; }
        .sidebar-cta-eyebrow { font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--accent); margin-bottom: 10px; }
        .sidebar-cta-headline { font-family: var(--font-display); font-size: 20px; letter-spacing: -0.5px; line-height: 1.2; color: var(--white); margin-bottom: 10px; }
        .sidebar-cta-desc { font-size: 13px; color: rgba(255,255,255,0.5); line-height: 1.6; margin-bottom: 20px; }
        .btn-sidebar { display: inline-flex; align-items: center; gap: 7px; font-family: var(--font-body); font-size: 13px; font-weight: 500; padding: 10px 20px; background: var(--accent); color: var(--ink); border: none; border-radius: var(--r-pill); cursor: pointer; transition: opacity 0.15s, transform 0.15s; text-decoration: none; width: 100%; justify-content: center; }
        .btn-sidebar:hover { opacity: 0.88; transform: translateY(-1px); }
        .sidebar-stat { background: var(--white); border: 1px solid var(--border); border-radius: var(--r-lg); padding: 20px 24px; text-align: center; }
        .sidebar-stat-number { font-family: var(--font-display); font-size: 40px; color: var(--ink); letter-spacing: -2px; line-height: 1; margin-bottom: 6px; }
        .sidebar-stat-number span { color: var(--accent); }
        .sidebar-stat-label { font-size: 12px; color: var(--ink-soft); line-height: 1.5; }

        /* RELATED */
        .related-section { margin-top: 80px; padding-top: 56px; border-top: 1px solid var(--border); }
        .section-label { font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--ink-faint); margin-bottom: 24px; display: flex; align-items: center; gap: 10px; }
        .section-label::after { content: ''; flex: 1; height: 1px; background: var(--border); max-width: 80px; }
        .related-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
        .related-card { background: var(--white); border: 1px solid var(--border); border-radius: var(--r-lg); overflow: hidden; text-decoration: none; color: inherit; transition: transform 0.2s, box-shadow 0.2s; display: flex; flex-direction: column; }
        .related-card:hover { transform: translateY(-3px); box-shadow: var(--shadow-md); }
        .related-thumb { height: 140px; display: flex; align-items: center; justify-content: center; overflow: hidden; }
        .related-thumb img { width: 100%; height: 100%; object-fit: cover; }
        .related-body { padding: 18px 18px 14px; flex: 1; }
        .related-cat { font-size: 10px; font-weight: 700; color: var(--ink-faint); letter-spacing: 1px; text-transform: uppercase; margin-bottom: 8px; }
        .related-title { font-family: var(--font-display); font-size: 16px; letter-spacing: -0.3px; line-height: 1.3; color: var(--ink); }
        .related-meta { padding: 10px 18px; border-top: 1px solid var(--border); font-size: 11px; color: var(--ink-faint); display: flex; justify-content: space-between; }

        /* RESPONSIVE */
        @media (max-width: 1100px) {
          :root { --content-width: 100%; }
          .content-row { grid-template-columns: 1fr; gap: 40px; }
          .sidebar { position: static; }
          .toc { display: none; }
          .related-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 720px) {
          .nav { padding: 0 20px; }
          .nav-links { display: none; }
          .breadcrumb-bar { padding: 0 20px; }
          .page-wrap { padding: 0 20px 80px; }
          .post-header-section { padding-top: 36px; }
          .post-meta-row { gap: 10px; }
          .meta-share { display: none; }
          .related-grid { grid-template-columns: 1fr; }
          .inline-cta { flex-direction: column; }
          .stat-callout { flex-direction: column; gap: 12px; }
          .stat-callout-number { font-size: 36px; }
        }
      `}</style>

      {/* PROGRESS BAR */}
      <div id="progress-bar" ref={progressRef} role="progressbar" aria-label="Reading progress" />

      {/* NAV */}
      <nav className="nav" aria-label="Main navigation">
        <div className="nav-inner">
          <Link to="/" className="nav-logo">FrameFinder</Link>
          <div className="nav-links">
            <Link to="/blog" className="nav-link">&#8592; Blog</Link>
          </div>
          <div className="nav-actions">
            <Link to="/login" className="btn btn-ghost">Log in</Link>
            <Link to="/signup" className="btn btn-primary">
              Create your profile
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </Link>
          </div>
        </div>
      </nav>

      {/* BREADCRUMB */}
      <div className="breadcrumb-bar">
        <div className="breadcrumb-inner">
          <Link to="/" className="breadcrumb-item">Home</Link>
          <span className="breadcrumb-sep" aria-hidden="true">/</span>
          <Link to="/blog" className="breadcrumb-item">Blog</Link>
          {post.category && (
            <>
              <span className="breadcrumb-sep" aria-hidden="true">/</span>
              <Link to={`/blog/category/${post.category.slug}`} className="breadcrumb-item">{post.category.title}</Link>
            </>
          )}
          <span className="breadcrumb-sep" aria-hidden="true">/</span>
          <span className="breadcrumb-item current">{post.title?.substring(0, 40)}{post.title?.length > 40 ? '...' : ''}</span>
        </div>
      </div>

      {/* MAIN */}
      <main>
        <div className="page-wrap">

          {/* POST HEADER */}
          <header className="post-header-section">
            {post.category && (
              <Link
                to={`/blog/category/${post.category.slug}`}
                className="post-cat-badge"
                style={{ background: badge.bg, color: badge.text }}
              >
                <span className="cat-dot" style={{ background: badge.dot }} />
                {post.category.title}
              </Link>
            )}

            <h1 className="post-title">
              {post.title}
              {post.italicSubtitle && <> <em>({post.italicSubtitle})</em></>}
            </h1>

            <p className="post-intro">{post.intro}</p>

            <div className="post-meta-row">
              <div className="meta-author">
                <div className="meta-avatar" aria-hidden="true">
                  <span className="meta-avatar-text">Ff</span>
                </div>
                <span className="meta-author-name">FrameFinder Editorial</span>
              </div>
              <span className="meta-dot" aria-hidden="true" />
              <time className="meta-item" dateTime={post.publishedAt}>{publishedDate}</time>
              <span className="meta-dot" aria-hidden="true" />
              <span className="meta-item">{post.readTime} min read</span>
              <div className="meta-share" aria-label="Share this article">
                <span className="share-label">Share</span>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(post.seoCanonicalUrl)}`}
                  className="share-btn" aria-label="Share on LinkedIn" target="_blank" rel="noopener noreferrer"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </a>
                <button className="share-btn" aria-label="Copy link" onClick={copyLink}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                </button>
              </div>
            </div>
          </header>

          {/* CONTENT ROW */}
          <div className="content-row">

            {/* ARTICLE COLUMN */}
            <div>

              {/* HERO IMAGE */}
              <figure>
                <div
                  className="hero-image"
                  role="img"
                  aria-label={post.heroImageAlt}
                  style={!heroSrc ? { background: SEGMENT_GRADIENT[post.category?.segment] || SEGMENT_GRADIENT['photographers'] } : {}}
                >
                  {heroSrc && <img src={heroSrc} alt={post.heroImageAlt} />}
                </div>
                {(post.heroImageCaption || post.heroImageCredit) && (
                  <figcaption className="hero-caption">
                    {post.heroImageCaption}{post.heroImageCredit && ` ${post.heroImageCredit}`}
                  </figcaption>
                )}
              </figure>

              {/* TAKEAWAYS */}
              <div className="takeaways-box" id="key-takeaways">
                <div className="takeaways-label">Key Takeaways</div>
                <ul>
                  {(post.takeaways || []).map((t, i) => <li key={i}>{t}</li>)}
                </ul>
              </div>

              {/* ARTICLE BODY */}
              <article className="post-body" id="article-body">
                {post.body && (
                  <PortableText value={post.body} components={ptComponents} />
                )}
              </article>

              {/* POST FOOTER */}
              <footer className="post-footer">
                {/* Tags */}
                {post.tags && post.tags.length > 0 && (
                  <div className="post-tags">
                    <span className="tags-label">Topics:</span>
                    {post.category && (
                      <Link to={`/blog/category/${post.category.slug}`} className="tag">{post.category.title}</Link>
                    )}
                    {post.tags.map(tag => (
                      <Link key={tag.slug} to={`/blog/tag/${tag.slug}`} className="tag">{tag.title}</Link>
                    ))}
                  </div>
                )}

                {/* Author bio */}
                <div className="author-bio" itemScope itemType="https://schema.org/Person">
                  <div className="author-bio-avatar" aria-hidden="true">
                    <span className="author-bio-avatar-text">Ff</span>
                  </div>
                  <div className="author-bio-content">
                    <div className="author-bio-name" itemProp="name">FrameFinder Editorial</div>
                    <div className="author-bio-role">Denver, Colorado</div>
                    <p className="author-bio-text" itemProp="description">
                      {post.authorBioText || DEFAULT_BIO}
                    </p>
                  </div>
                </div>
              </footer>
            </div>

            {/* SIDEBAR */}
            <aside className="sidebar" aria-label="Article navigation and resources">

              {/* TOC */}
              {toc.length > 0 && (
                <nav className="toc" aria-label="Table of contents">
                  <div className="toc-heading">On this page</div>
                  <ul className="toc-list" role="list">
                    {toc.map(item => (
                      <li key={item.id} className="toc-item">
                        <a
                          href={`#${item.id}`}
                          className={`toc-link${item.level === 'h3' ? ' toc-link-sub' : ''}${activeId === item.id ? ' active' : ''}`}
                          onClick={e => scrollToSection(e, item.id)}
                        >
                          {item.text}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              )}

              {/* SIDEBAR STAT */}
              <div className="sidebar-stat">
                <div className="sidebar-stat-number">
                  {post.sidebarStatNumber && <span style={{ color: 'var(--ink)' }}>{post.sidebarStatNumber}</span>}
                  {post.sidebarStatNumberAccent && <span>{post.sidebarStatNumberAccent}</span>}
                </div>
                <div className="sidebar-stat-label">{post.sidebarStatLabel}</div>
              </div>

              {/* SIDEBAR CTA */}
              <div className="sidebar-cta">
                <div className="sidebar-cta-eyebrow">{post.sidebarCtaEyebrow}</div>
                <div className="sidebar-cta-headline">{post.sidebarCtaHeadline}</div>
                <div className="sidebar-cta-desc">{post.sidebarCtaDescription}</div>
                <a href={post.sidebarCtaButtonUrl} className="btn-sidebar">
                  {post.sidebarCtaButtonLabel}
                  <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </a>
              </div>

            </aside>
          </div>

          {/* RELATED POSTS */}
          {related.length > 0 && (
            <section className="related-section" aria-label="Related articles">
              <div className="section-label">{relatedLabel}</div>
              <div className="related-grid">
                {related.map(rel => {
                  const relGradient = SEGMENT_GRADIENT[rel.category?.segment] || SEGMENT_GRADIENT['photographers']
                  const relSrc = rel.heroImage ? urlFor(rel.heroImage).width(400).height(280).url() : null
                  return (
                    <Link key={rel.slug} to={`/blog/${rel.slug}`} className="related-card">
                      <div className="related-thumb" style={{ background: relGradient }}>
                        {relSrc && <img src={relSrc} alt={rel.heroImageAlt || rel.title} />}
                      </div>
                      <div className="related-body">
                        {rel.category && <div className="related-cat">{rel.category.title}</div>}
                        <h3 className="related-title">{rel.title}</h3>
                      </div>
                      <div className="related-meta">
                        <span>{formatDateShort(rel.publishedAt)}</span>
                        <span>{rel.readTime} min read</span>
                      </div>
                    </Link>
                  )
                })}
              </div>
            </section>
          )}

        </div>
      </main>

      {/* FOOTER */}
      <footer style={{ background: 'var(--ink)', padding: '48px 40px', marginTop: 0 }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 20 }}>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 20, color: 'var(--white)' }}>FrameFinder</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Link to="/" style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', textDecoration: 'none', padding: '4px 10px', borderRadius: 100 }}>For Businesses</Link>
            <Link to="/?view=photographer" style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', textDecoration: 'none', padding: '4px 10px', borderRadius: 100 }}>For Creatives</Link>
            <Link to="/blog" style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', textDecoration: 'none', padding: '4px 10px', borderRadius: 100 }}>Blog</Link>
          </div>
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.2)' }}>&copy; 2026 FrameFinder &middot; Denver, CO</span>
        </div>
      </footer>
    </>
  )
}

export default BlogPost
